// filepath: apps/web/src/renderers/form/FieldRegistry.ts
//
// FieldRegistry — the FR-FORM-3 AC-4 extension surface.
//
// "Adding a new standard field type to the renderer requires only a
//  frontend change, not per-tenant code."
//
// Usage:
//
//   // 1. Create src/renderers/form/fields/MyField.tsx
//   export function MyField(props: FieldComponentProps) { ... }
//
//   // 2. Register it (typically in src/renderers/form/fields/index.ts)
//   import { registerField } from './FieldRegistry';
//   registerField('my_type', MyField);
//
//   // 3. The renderer picks it up automatically — no definition change
//      required (FR-FORM-3 AC-3 + AC-4).
//
// SECURITY:
//   - Renderer-side field filtering is NEVER a security boundary. The
//     server strips fields the caller may not read (FR-AUTH-7, INV-2);
//     the renderer renders what it receives.
//   - Field components render tenant-authored text as text nodes, never
//     as HTML (INV-9).

import React from "react";
import type { ComponentType } from "react";
import type {
  FieldMetadata,
  UiSchema,
  UiSchemaField,
} from "@/definitions/types";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";

import { StringField } from "./fields/StringField";
import { NumberField } from "./fields/NumberField";
import { IntegerField } from "./fields/IntegerField";
import { BooleanField } from "./fields/BooleanField";
import { DateField } from "./fields/DateField";
import { DateTimeField } from "./fields/DateTimeField";
import { EnumField } from "./fields/EnumField";
import { ReferenceField } from "./fields/ReferenceField";
import { HiddenField } from "./fields/HiddenField";
// FR-UI-CORE-7 AC-2: rich-text field. Sanitized via DOMPurify; renders
// without dangerouslySetInnerHTML (INV-9). See richText/RichText.tsx
// for the per-tag mapping and richText/sanitizeConfig.ts for the
// single-source-of-truth allowlist.
import { RichText } from "./fields/richText/RichText";

export interface FieldComponentProps {
  fieldId: string;
  metadata: FieldMetadata;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  /**
   * Per-form UiSchema. FR-UI-CORE-9 wire extension: field components
   * read `uiSchema.fields[fieldId].label` / `.help` / `.required` to
   * render locale-aware labels and the `aria-required` attribute.
   * Optional — when absent, the renderer falls back to fieldId-as-label.
   */
  uiSchema?: UiSchema | null;
  /**
   * FR-UI-CORE-10 AC-5 — per-field validation error. Populated by
   * the FormRenderer when the server returns HTTP 422 with the
   * matching fieldId in its `errors` envelope. Field components opt
   * in to applying `aria-invalid` + `aria-describedby` to their
   * input element when this prop is non-null. When the error map
   * clears (next successful submit), this prop is `null` and the
   * field component removes the aria attributes.
   */
  fieldError?: {
    /** Pre-resolved message string for the active locale. */
    message: string;
    /** DOM id used by the matching InlineFieldError wrapper. */
    id: string;
    /** Machine-readable error code. */
    code?: string;
  } | null;
}

export type FieldComponent = ComponentType<FieldComponentProps>;

/**
 * Fallback component for unknown field types. Renders the label and the
 * raw value (or empty) as text. NEVER evaluated; NEVER used as a security
 * boundary — the server has already stripped unauthorized fields (INV-2).
 *
 * FR-UI-CORE-9 wire extension: label resolved from uiSchema.
 */
export function UnknownField({
  fieldId,
  metadata,
  value,
  uiSchema,
}: FieldComponentProps): React.ReactElement {
  const { locale } = useTranslation();
  const uiField = getUiSchemaField(uiSchema, fieldId);
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);
  return (
    <div
      data-testid={`field-${fieldId}-unknown`}
      className="field field-unknown"
    >
      <label htmlFor={fieldId}>
        {labelText}
        {uiField?.required ? <span aria-hidden="true">{" *"}</span> : null}
      </label>
      <span
        className="field-unknown-type"
        data-testid={`field-${fieldId}-unknown-type`}
      >
        (unknown type: {String(metadata.fieldType)})
      </span>
      <input
        id={fieldId}
        type="text"
        value={value === undefined || value === null ? "" : String(value)}
        readOnly
        aria-readonly="true"
        aria-required={uiField?.required ? "true" : undefined}
      />
    </div>
  );
}

/**
 * Registry of field-type → component. Built-in entries are seeded lazily
 * (on first access) by `seedBuiltinFields()` to break module-load cycles.
 *
 * Extension: call `registerField('my_type', MyComponent)` to add a new
 * type. The renderer will use it for any field whose `fieldType` matches.
 */
class FieldRegistryImpl {
  private readonly components = new Map<string, FieldComponent>();
  private seeded = false;

  register(fieldType: string, component: FieldComponent): void {
    this.components.set(fieldType, component);
  }

  get(fieldType: string): FieldComponent | undefined {
    if (!this.seeded) {
      this.seeded = true;
      seedBuiltinFields(this);
    }
    return this.components.get(fieldType);
  }

  has(fieldType: string): boolean {
    return this.get(fieldType) !== undefined;
  }

  /** Returns the list of registered field types (sorted, for diagnostics). */
  list(): string[] {
    if (!this.seeded) {
      this.seeded = true;
      seedBuiltinFields(this);
    }
    return [...this.components.keys()].sort();
  }

  /** Test-only: clear all entries (including builtins) so each test starts clean. */
  __resetForTests(): void {
    this.components.clear();
    this.seeded = false;
  }
}

function seedBuiltinFields(reg: FieldRegistryImpl): void {
  reg.register("string", StringField);
  reg.register("text", StringField); // legacy alias per D-2
  reg.register("number", NumberField);
  reg.register("float", NumberField); // legacy alias
  reg.register("integer", IntegerField);
  reg.register("boolean", BooleanField);
  reg.register("date", DateField);
  reg.register("datetime", DateTimeField);
  reg.register("enum", EnumField);
  reg.register("reference", ReferenceField);
  reg.register("hidden", HiddenField);
  // FR-UI-CORE-7 AC-2: rich-text field registered here so the
  // FormRenderer picks it up automatically when a form definition
  // declares `fieldType: "text_html"`. The component sanitises via
  // DOMPurify and renders via the per-tag JSX mapper — see
  // ./fields/richText/RichText.tsx.
  reg.register("text_html", RichText);
}

/** Module-level singleton. Mutations are visible across the app. */
export const fieldRegistry = new FieldRegistryImpl();

/** Convenience wrapper around `fieldRegistry.register`. */
export function registerField(
  fieldType: string,
  component: FieldComponent,
): void {
  fieldRegistry.register(fieldType, component);
}

/**
 * Look up the per-field UiSchema entry. Returns `undefined` when the
 * form has no uiSchema or no entry for this field id. Field
 * components call this rather than indexing `uiSchema.fields` directly
 * to keep the optional-chain noise out of every renderer.
 */
export function getUiSchemaField(
  uiSchema: UiSchema | null | undefined,
  fieldId: string,
): UiSchemaField | undefined {
  return uiSchema?.fields?.[fieldId];
}
